import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { PlusIcon, ScanLineIcon, SearchIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { NAV_GROUPS } from '@/components/nav-items'
import { partsKey, fetchParts } from '@/features/parts/api'

/**
 * Somewhere to type instead of reaching for the mouse.
 *
 * Ctrl/⌘+K opens it; `/` focuses the search box; `g` followed by a letter jumps
 * to a page. All of it stays out of the way while something is being typed into
 * — a shortcut that fires inside a text field is a shortcut that eats text.
 */
export function CommandPalette() {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [helpOpen, setHelpOpen] = useState(false)

  useGlobalShortcuts({
    onPalette: () => setOpen((current) => !current),
    onHelp: () => setHelpOpen(true),
    onSearch: focusSearch,
    onGoTo: (path) => void navigate({ to: path }),
  })

  // Only search once there is something worth searching for; the palette is
  // mostly used to navigate, and a request per keystroke from the first letter
  // is a lot of noise for that.
  const trimmed = query.trim()
  const parts = useQuery({
    queryKey: partsKey({ q: trimmed, per_page: 8 }),
    queryFn: () => fetchParts({ q: trimmed, per_page: 8 }),
    enabled: open && trimmed.length >= 2,
  })

  function go(action: () => void) {
    setOpen(false)
    setQuery('')
    action()
  }

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="overflow-hidden p-0 sm:max-w-lg">
          <DialogHeader className="sr-only">
            <DialogTitle>Command palette</DialogTitle>
            <DialogDescription>Search parts or jump to a page.</DialogDescription>
          </DialogHeader>
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search parts, or jump to…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandList>
              <CommandEmpty>Nothing matches.</CommandEmpty>

              {(parts.data?.items.length ?? 0) > 0 && (
                <CommandGroup heading="Parts">
                  {parts.data?.items.map((part) => (
                    <CommandItem
                      key={part.id}
                      value={`part-${part.id}`}
                      onSelect={() =>
                        go(
                          () =>
                            void navigate({ to: '/parts/$partId', params: { partId: part.id } }),
                        )
                      }
                    >
                      <SearchIcon className="size-4" />
                      <span className="truncate">{part.name}</span>
                      <span className="text-muted-foreground ml-auto truncate text-xs">
                        {part.category_name}
                      </span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}

              <CommandGroup heading="Actions">
                <CommandItem
                  value="new-part"
                  onSelect={() => go(() => void navigate({ to: '/parts/new' }))}
                >
                  <PlusIcon className="size-4" />
                  New part
                </CommandItem>
                <CommandItem value="scan" onSelect={() => go(() => void navigate({ to: '/scan' }))}>
                  <ScanLineIcon className="size-4" />
                  Scan
                </CommandItem>
                {trimmed && (
                  <CommandItem
                    value="search-parts"
                    onSelect={() =>
                      go(() => void navigate({ to: '/parts', search: { q: trimmed } }))
                    }
                  >
                    <SearchIcon className="size-4" />
                    Search parts for “{trimmed}”
                  </CommandItem>
                )}
              </CommandGroup>

              <CommandSeparator />

              {NAV_GROUPS.map((group) => (
                <CommandGroup key={group.label} heading={group.label}>
                  {group.items.map((item) => (
                    <CommandItem
                      key={item.to}
                      value={`go-${item.to}`}
                      onSelect={() => go(() => void navigate({ to: item.to }))}
                    >
                      <item.icon className="size-4" />
                      {item.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </DialogContent>
      </Dialog>

      <ShortcutHelp open={helpOpen} onOpenChange={setHelpOpen} />
    </>
  )
}

/** Where each `g …` shortcut goes. */
export const GO_TO: Record<string, string> = {
  p: '/parts',
  j: '/projects',
  s: '/scan',
  l: '/locations',
  d: '/',
  a: '/activity',
}

/** How long after `g` another key still counts as part of the sequence. */
export const SEQUENCE_MS = 1000

/** True while the person is typing into something. */
export function isTypingInto(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)
}

function focusSearch() {
  const search = document.querySelector<HTMLInputElement>('input[type="search"]')
  search?.focus()
  search?.select()
}

function useGlobalShortcuts({
  onPalette,
  onHelp,
  onSearch,
  onGoTo,
}: {
  onPalette: () => void
  onHelp: () => void
  onSearch: () => void
  onGoTo: (path: string) => void
}) {
  useEffect(() => {
    let pendingGo = 0

    function onKeyDown(event: KeyboardEvent) {
      // Ctrl/⌘+K works even inside a field: it is how you get *out* of one.
      if (event.key.toLowerCase() === 'k' && (event.metaKey || event.ctrlKey)) {
        event.preventDefault()
        onPalette()
        return
      }

      if (isTypingInto(event.target) || event.metaKey || event.ctrlKey || event.altKey) return

      if (event.key === '/') {
        event.preventDefault()
        onSearch()
        return
      }

      if (event.key === '?') {
        event.preventDefault()
        onHelp()
        return
      }

      const now = event.timeStamp || performance.now()
      if (event.key === 'g') {
        pendingGo = now
        return
      }

      if (pendingGo && now - pendingGo <= SEQUENCE_MS) {
        const destination = GO_TO[event.key.toLowerCase()]
        pendingGo = 0
        if (destination) {
          event.preventDefault()
          onGoTo(destination)
        }
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [onGoTo, onHelp, onPalette, onSearch])
}

const SHORTCUTS: [string, string][] = [
  ['Ctrl / ⌘ + K', 'Open the command palette'],
  ['/', 'Focus the search box'],
  ['g then p', 'Go to parts'],
  ['g then j', 'Go to projects'],
  ['g then s', 'Go to scan'],
  ['g then l', 'Go to storage'],
  ['g then a', 'Go to activity'],
  ['g then d', 'Go to the dashboard'],
  ['?', 'Show this list'],
]

function ShortcutHelp({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>None of these fire while you are typing.</DialogDescription>
        </DialogHeader>
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {SHORTCUTS.map(([keys, what]) => (
            <div key={keys} className="contents">
              <dt>
                <kbd className="bg-muted rounded px-1.5 py-0.5 text-xs">{keys}</kbd>
              </dt>
              <dd className="text-muted-foreground">{what}</dd>
            </div>
          ))}
        </dl>
      </DialogContent>
    </Dialog>
  )
}
